import { Injectable, SecurityContext } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer } from '@angular/platform-browser';
import { CustomToastComponent } from '../shared/custom-toast/custom-toast.component';
import type { InterpolationParameters } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ToastService {
    public isPersistentToastActive = false;
    constructor(private toastrService: ToastrService, private translateService: TranslateService, private sanitizer: DomSanitizer) {
        this.setToastrConfig();
    }

    private getMessage(message: string, shouldTranslate = false, params: InterpolationParameters = {}): string {
        if (shouldTranslate) {
            return this.translateService.instant(message, params);
        }

        return message;
    }

    private setToastrConfig() {
        this.toastrService.toastrConfig.enableHtml = true;
        this.toastrService.toastrConfig.closeButton = true;
        this.toastrService.toastrConfig.extendedTimeOut = 2000;
        this.toastrService.toastrConfig.tapToDismiss = false;
        this.toastrService.toastrConfig.newestOnTop = false;
    }

    showSuccess(message: string, shouldTranslate = false, params: InterpolationParameters = {}) {
        this.toastrService.success(this.getMessage(message, shouldTranslate, params));
    }

    showSuccessWithDisableTimeOut(message: string, shouldTranslate = false, title = '', params: InterpolationParameters = {}) {
        const translatedMessage = this.getMessage(message, shouldTranslate, params);
        this.isPersistentToastActive = true;

        const toastRef = this.toastrService.success(translatedMessage, title, {
            disableTimeOut: true,
            toastComponent: CustomToastComponent,
        });

        toastRef.onHidden.subscribe(() => {
            this.isPersistentToastActive = false;
        });
    }

    showWarning(message: string, shouldTranslate  = false, params: InterpolationParameters = {}) {
        this.toastrService.warning(this.getMessage(message, shouldTranslate, params));
    }

    showMessage(message: string, shouldTranslate  = false, params: InterpolationParameters = {}) {
        this.toastrService.show(this.getMessage(message, shouldTranslate, params));
    }

    showInfo(message: string, shouldTranslate  = false, params: InterpolationParameters = {}) {
        this.toastrService.info(this.getMessage(message, shouldTranslate, params));
    }

    showError(error: any, message: string = 'An error has occurred') {
        const isShown = this.displayError(error);

        if (!isShown) {
            this.displayErrorMessage(message);
        }
    }

    showErrors(error: any, properties: string[], message = 'An error has occurred') {
        let isShown = this.displayError(error);

        if (!isShown && error.error && error.error.errors) {
            const errors = error.error.errors;
            properties.forEach(property => {
                if (errors[property]) {
                    isShown = true;
                    this.displayErrorMessage(errors[property]);
                }
            });
        }

        if (!isShown) {
            this.displayErrorMessage(message);
        }
    }

    private displayError(error: any): boolean {
        if (error.error && error.error.message) {
            this.displayErrorMessage(error.error.message);
            return true;
        } else if (error.error && typeof error.error === 'string') {
            this.displayErrorMessage(error.error);
            return true;
        } else if (error.error && error.error.errors) {
            Object.values(error.error.errors).forEach((msg: any) => {
                this.displayErrorMessage(msg);
            });
            return true;
        }
        return false;
    }

    displayErrorMessage(message: string, shouldTranslate  = false, params: InterpolationParameters = {}) {
        const resolved = this.getMessage(message, shouldTranslate, params);
        const safe = this.sanitizer.sanitize(SecurityContext.HTML, resolved) ?? '';
        this.toastrService.error(safe);
    }
}
